import React, { useState, useEffect } from 'react';
import { CodeListConfig, CodeListKey, CODE_LISTS } from '../useCodeList';
import { ManagementListSkeleton } from './ManagementListSkeleton';
import { Pagination } from '../../../common/components/Pagination';
import { PrimaryButton } from '../../../common/elements/Buttons/PrimaryButton';
import { SecondaryButton } from '../../../common/elements/Buttons/SecondaryButton';
import {
  ContentCard,
  SelectWrapper,
  StyledSelect,
  TableCard,
  StyledTable,
  StyledTableHead,
  StyledTableBody,
  ActionCell,
  IconButton,
  PaginationWrapper,
  EmptyState,
  Badge,
  ModalOverlay,
  ModalContent,
  ModalTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormCheckbox,
  ModalActions
} from '../styles';

type Props = {
  config: CodeListConfig;
  selectedCodeList: CodeListKey;
  data: any[];
  count: number;
  loading: boolean;
  page: number;
  offset: number;
  limit: number;
  modalOpen: boolean;
  editingItem: any | null;
  confirmDeleteId: number | null;
  onCodeListChange: (key: CodeListKey) => void;
  onPagination: (offset: number, page: number) => void;
  onLimitChange: (limit: number) => void;
  onOpenCreate: () => void;
  onOpenEdit: (item: any) => void;
  onCloseModal: () => void;
  onSave: (formData: Record<string, any>) => void;
  onRequestDelete: (id: number) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
};

const getValue = (obj: any, path: string) => {
  return path.split('.').reduce(
    (acc, part) => acc?.[part],
    obj
  );
}

const setValue = (obj: any, path: string, value: any) => {
  const parts = path.split('.');
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];

    if (current[key] == null) {
      current[key] = {};
    }

    current = current[key];
  }

  current[parts[parts.length - 1]] = value;
}

// Formulářový modal
const FormModal: React.FC<{
  config: CodeListConfig;
  editingItem: any | null;
  onSave: (data: Record<string, any>) => void;
  onClose: () => void;
}> = ({ config, editingItem, onSave, onClose }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});

  useEffect(() => {
    const initial: Record<string, any> = {};

    config.fields.forEach((f) => {
      const value = editingItem
        ? getValue(editingItem, f.key)
        : undefined;

      setValue(
        initial,
        f.key,
        value ??
        (f.type === 'checkbox'
          ? !editingItem
          : '')
      );
    });

    setFormData(initial);
  }, [editingItem, config.fields]);

  const handleChange = (key: string, value: any) => {
    setFormData((prev) => {
      const copy = structuredClone(prev);

      setValue(copy, key, value);

      return copy;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const isValid = config.fields
    .filter((f) => f.required)
    .every((f) => {
      const value = getValue(formData, f.key);

      return value !== undefined && value !== '';
    });

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>
          {editingItem ? 'Upravit záznam' : 'Nový záznam'} — {config.label}
        </ModalTitle>

        <form onSubmit={handleSubmit}>
          {config.fields.map((field) =>
            field.type === 'checkbox' ? (
              <FormGroup key={field.key}>
                <FormCheckbox>
                  <input
                    type="checkbox"
                    id={`field-${field.key}`}
                    checked={!!getValue(formData, field.key)}
                    onChange={(e) =>
                      handleChange(field.key, e.target.checked)
                    }
                  />
                  <label htmlFor={`field-${field.key}`}>
                    {field.label}
                  </label>
                </FormCheckbox>
              </FormGroup>
            ) : (
              <FormGroup key={field.key}>
                <FormLabel htmlFor={`field-${field.key}`}>
                  {field.label}
                  {field.required && ' *'}
                </FormLabel>

                <FormInput
                  id={`field-${field.key}`}
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={getValue(formData, field.key) ?? ''}
                  onChange={(e) =>
                    handleChange(
                      field.key,
                      field.type === 'number'
                        ? e.target.value === ''
                          ? ''
                          : Number(e.target.value)
                        : e.target.value
                    )
                  }
                  required={field.required}
                />
              </FormGroup>
            )
          )}

          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>
              Zrušit
            </SecondaryButton>

            <PrimaryButton type="submit" disabled={!isValid}>
              {editingItem ? 'Uložit' : 'Vytvořit'}
            </PrimaryButton>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// Potvrzovací dialog pro smazání
const DeleteConfirm: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => (
  <ModalOverlay onClick={onCancel}>
    <ModalContent onClick={(e) => e.stopPropagation()}>
      <ModalTitle>Smazat záznam</ModalTitle>
      <p>Opravdu chcete smazat tento záznam? Tuto akci nelze vrátit zpět.</p>
      <ModalActions>
        <SecondaryButton type="button" onClick={onCancel}>
          Zrušit
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onConfirm}>
          Smazat
        </PrimaryButton>
      </ModalActions>
    </ModalContent>
  </ModalOverlay>
);

// Renderování hodnoty buňky
const renderCellValue = (col: any, row: any) => {
  if (col.render) return col.render(row[col.key], row);
  const value = row[col.key];
  if (col.key === 'is_active' || typeof value === 'boolean') {
    return <Badge active={!!value}>{value ? 'Ano' : 'Ne'}</Badge>;
  }
  return value ?? '—';
};

export const CodeListManager: React.FC<Props> = ({
  config,
  selectedCodeList,
  data,
  count,
  loading,
  page,
  offset,
  limit,
  modalOpen,
  editingItem,
  confirmDeleteId,
  onCodeListChange,
  onPagination,
  onLimitChange,
  onOpenCreate,
  onOpenEdit,
  onCloseModal,
  onSave,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete
}) => {
  return (
    <>
      <SelectWrapper>
        <StyledSelect
          value={selectedCodeList}
          onChange={(e) => onCodeListChange(e.target.value as CodeListKey)}>
          {CODE_LISTS.map((cl) => (
            <option key={cl.key} value={cl.key}>
              {cl.label}
            </option>
          ))}
        </StyledSelect>
      </SelectWrapper>

      <ContentCard>
        <h3 style={{ margin: '0 0 1rem', fontSize: '16px', fontWeight: 600 }}>{config.label}</h3>

        <TableCard>
          <StyledTable>
            <StyledTableHead>
              <tr>
                {config.columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th style={{ width: 80, textAlign: 'center' }}>Akce</th>
              </tr>
            </StyledTableHead>
            <StyledTableBody>
              {loading ? (
                <ManagementListSkeleton columns={config.columns.length + 1} />
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={config.columns.length + 1}>
                    <EmptyState>Žádné záznamy</EmptyState>
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id} className={row.hasOwnProperty('is_active') && !row.is_active ? 'inactive' : ''}>
                    {config.columns.map((col) => (
                      <td key={col.key}>{renderCellValue(col, row)}</td>
                    ))}
                    <ActionCell>
                      <IconButton variant="edit" title="Upravit" onClick={() => onOpenEdit(row)}>
                        ✏️
                      </IconButton>
                    </ActionCell>
                  </tr>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </TableCard>

        <div style={{ marginTop: '1rem' }}>
          <PrimaryButton onClick={onOpenCreate}>+ Nový záznam</PrimaryButton>
        </div>

        {count > 0 && (
          <PaginationWrapper>
            <Pagination
              count={count}
              offset={offset}
              page={page}
              limit={limit}
              onSubmit={onPagination}
              onLimitChange={onLimitChange}
            />
          </PaginationWrapper>
        )}
      </ContentCard>

      {modalOpen && (
        <FormModal
          config={config}
          editingItem={editingItem}
          onSave={onSave}
          onClose={onCloseModal}
        />
      )}

      {confirmDeleteId !== null && (
        <DeleteConfirm onConfirm={onConfirmDelete} onCancel={onCancelDelete} />
      )}
    </>
  );
};
